/**
 * ANT Credit wallet — balance, packs, checkout, capacity usage / expand.
 */
import {createSlice} from "@reduxjs/toolkit";
import apiService from "../../api/apiService";
import {endPoints} from "../../api/endpoints";
import {formatAxiosErrorMessage, qargsToQstring} from "../../utils/common";
import {getApiErrorBody} from "../../utils/capacity";

const unwrap = (payload) => payload?.data ?? payload ?? null;

/** Normalize ledger list from paginated or bare-array responses. */
const normalizeLedger = (payload, {append = false, prev = null} = {}) => {
    const body = unwrap(payload);
    let results = [];
    let next = null;
    let count = null;
    if (Array.isArray(body)) {
        results = body;
    } else if (body && typeof body === "object") {
        results = Array.isArray(body.results)
            ? body.results
            : Array.isArray(body.items)
                ? body.items
                : Array.isArray(body.entries)
                    ? body.entries
                    : Array.isArray(body.transactions)
                        ? body.transactions
                        : Array.isArray(body.data)
                            ? body.data
                            : [];
        next = body.next ?? null;
        count = body.count ?? null;
    }
    if (append && Array.isArray(prev?.results)) {
        const seen = new Set(prev.results.map((r) => r.id ?? JSON.stringify(r)));
        const merged = [...prev.results];
        results.forEach((row) => {
            const key = row.id ?? JSON.stringify(row);
            if (!seen.has(key)) {
                seen.add(key);
                merged.push(row);
            }
        });
        results = merged;
    }
    return {results, next, count};
};

const initialState = {
    balance: {isLoading: false, data: null, error: null},
    // { items: [], unit: { credits: 1, usd: 0.001 } }
    packs: {isLoading: false, data: [], unit: null, error: null},
    checkout: {isLoading: false, data: null, error: null},
    estimate: {isLoading: false, data: null, error: null},
    // GET /credits/capacity/ — used/limit + holds for FE progress bars
    capacity: {isLoading: false, data: null, error: null},
    expand: {isLoading: false, data: null, error: null},
    // GET /credits/ledger/ — credit + debit transactions
    ledger: {isLoading: false, data: null, error: null},
    // GET /credits/activities/ — rate card catalog
    activities: {isLoading: false, data: [], error: null},
    // GET /credits/usage/ — daily debit series for line chart
    usage: {isLoading: false, data: null, error: null},
};

export const creditsSlice = createSlice({
    name: "credits",
    initialState,
    reducers: {
        balanceRequesting: (state) => {
            state.balance.isLoading = true;
            state.balance.error = null;
        },
        balanceSuccess: (state, action) => {
            state.balance.isLoading = false;
            state.balance.data = unwrap(action.payload);
        },
        balanceError: (state, action) => {
            state.balance.isLoading = false;
            state.balance.error = action.payload;
        },
        packsRequesting: (state) => {
            state.packs.isLoading = true;
            state.packs.error = null;
        },
        packsSuccess: (state, action) => {
            state.packs.isLoading = false;
            const body = unwrap(action.payload);
            // New shape: { unit, packs: [...] }; legacy: bare array
            if (Array.isArray(body)) {
                state.packs.data = body;
                state.packs.unit = null;
            } else {
                state.packs.data = Array.isArray(body?.packs) ? body.packs : [];
                state.packs.unit = body?.unit || null;
            }
        },
        packsError: (state, action) => {
            state.packs.isLoading = false;
            state.packs.error = action.payload;
        },
        checkoutRequesting: (state) => {
            state.checkout.isLoading = true;
            state.checkout.error = null;
            state.checkout.data = null;
        },
        checkoutSuccess: (state, action) => {
            state.checkout.isLoading = false;
            state.checkout.data = unwrap(action.payload);
        },
        checkoutError: (state, action) => {
            state.checkout.isLoading = false;
            state.checkout.error = action.payload;
        },
        estimateRequesting: (state) => {
            state.estimate.isLoading = true;
            state.estimate.error = null;
        },
        estimateSuccess: (state, action) => {
            state.estimate.isLoading = false;
            state.estimate.data = unwrap(action.payload);
        },
        estimateError: (state, action) => {
            state.estimate.isLoading = false;
            state.estimate.error = action.payload;
        },
        capacityRequesting: (state) => {
            state.capacity.isLoading = true;
            state.capacity.error = null;
        },
        capacitySuccess: (state, action) => {
            state.capacity.isLoading = false;
            state.capacity.data = unwrap(action.payload);
            // Keep wallet balance in sync when capacity payload includes it
            const bal = state.capacity.data?.balance;
            if (bal) state.balance.data = bal;
        },
        capacityError: (state, action) => {
            state.capacity.isLoading = false;
            state.capacity.error = action.payload;
        },
        expandRequesting: (state) => {
            state.expand.isLoading = true;
            state.expand.error = null;
            state.expand.data = null;
        },
        expandSuccess: (state, action) => {
            state.expand.isLoading = false;
            state.expand.data = unwrap(action.payload);
            const bal = state.expand.data?.balance;
            if (bal) state.balance.data = bal;
        },
        expandError: (state, action) => {
            state.expand.isLoading = false;
            state.expand.error = action.payload;
        },
        expandReset: (state) => {
            state.expand = initialState.expand;
        },
        ledgerRequesting: (state) => {
            state.ledger.isLoading = true;
            state.ledger.error = null;
        },
        ledgerSuccess: (state, action) => {
            state.ledger.isLoading = false;
            const {append} = action.payload || {};
            state.ledger.data = normalizeLedger(action.payload?.raw ?? action.payload, {
                append: !!append,
                prev: state.ledger.data,
            });
        },
        ledgerError: (state, action) => {
            state.ledger.isLoading = false;
            state.ledger.error = action.payload;
        },
        ledgerReset: (state) => {
            state.ledger = initialState.ledger;
        },
        activitiesRequesting: (state) => {
            state.activities.isLoading = true;
            state.activities.error = null;
        },
        activitiesSuccess: (state, action) => {
            state.activities.isLoading = false;
            const body = unwrap(action.payload);
            state.activities.data = Array.isArray(body) ? body : [];
        },
        activitiesError: (state, action) => {
            state.activities.isLoading = false;
            state.activities.error = action.payload;
        },
        activitiesReset: (state) => {
            state.activities = initialState.activities;
        },
        usageRequesting: (state) => {
            state.usage.isLoading = true;
            state.usage.error = null;
        },
        usageSuccess: (state, action) => {
            state.usage.isLoading = false;
            state.usage.data = unwrap(action.payload);
        },
        usageError: (state, action) => {
            state.usage.isLoading = false;
            state.usage.error = action.payload;
        },
        usageReset: (state) => {
            state.usage = initialState.usage;
        },
    },
});

export const {
    balanceRequesting, balanceSuccess, balanceError,
    packsRequesting, packsSuccess, packsError,
    checkoutRequesting, checkoutSuccess, checkoutError,
    estimateRequesting, estimateSuccess, estimateError,
    capacityRequesting, capacitySuccess, capacityError,
    expandRequesting, expandSuccess, expandError, expandReset,
    ledgerRequesting, ledgerSuccess, ledgerError, ledgerReset,
    activitiesRequesting, activitiesSuccess, activitiesError, activitiesReset,
    usageRequesting, usageSuccess, usageError, usageReset,
} = creditsSlice.actions;

export const fetchCreditBalance = () => (dispatch) => {
    dispatch(balanceRequesting());
    apiService.get(endPoints.credits.balance)
        .then((res) => dispatch(balanceSuccess(res.data)))
        .catch((err) => dispatch(balanceError(formatAxiosErrorMessage(err))));
};

export const fetchCreditPacks = (currencyOrCountry = "USD") => (dispatch) => {
    dispatch(packsRequesting());
    const q = encodeURIComponent(String(currencyOrCountry || "USD"));
    apiService.get(`${endPoints.credits.packs}?currency=${q}`)
        .then((res) => dispatch(packsSuccess(res.data)))
        .catch((err) => dispatch(packsError(formatAxiosErrorMessage(err))));
};

export const checkoutCreditPack = (body) => (dispatch) => {
    dispatch(checkoutRequesting());
    return apiService.post(endPoints.credits.checkout, body)
        .then((res) => {
            dispatch(checkoutSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(checkoutError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const estimateWhatsappCredits = (body) => (dispatch) => {
    dispatch(estimateRequesting());
    return apiService.post(endPoints.credits.waEstimate, body)
        .then((res) => {
            dispatch(estimateSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(estimateError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/** Load used / limit / extras for storage, contacts, fields, tags. */
export const fetchCreditCapacity = () => (dispatch) => {
    dispatch(capacityRequesting());
    return apiService.get(endPoints.credits.capacity)
        .then((res) => {
            dispatch(capacitySuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(capacityError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/**
 * Spend credits for +capacity pack.
 * Body: { activity_code, quantity }
 */
export const expandCreditCapacity = (body) => (dispatch) => {
    dispatch(expandRequesting());
    return apiService.post(endPoints.credits.expand, body)
        .then((res) => {
            dispatch(expandSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            const apiBody = getApiErrorBody(err);
            const code = Array.isArray(apiBody?.code) ? apiBody.code[0] : apiBody?.code;
            // Structured insufficient_credits so UI can open pack checkout
            if (code === "insufficient_credits" || err?.response?.status === 402) {
                dispatch(expandError({
                    code: "insufficient_credits",
                    detail: formatAxiosErrorMessage(err),
                    required: apiBody?.required,
                    available: apiBody?.available,
                }));
            } else {
                dispatch(expandError(formatAxiosErrorMessage(err)));
            }
            throw err;
        });
};

/** Load credit/debit ledger. Pass append:true to merge the next page. */
export const fetchCreditLedger = (query = {}) => (dispatch) => {
    const {append, ...params} = query;
    dispatch(ledgerRequesting());
    const qs = qargsToQstring(params);
    return apiService.get(`${endPoints.credits.ledger}${qs}`)
        .then((res) => {
            dispatch(ledgerSuccess({raw: res.data, append: !!append}));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(ledgerError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/** Activity rate card — credits cost per billable action. */
export const fetchCreditActivities = () => (dispatch) => {
    dispatch(activitiesRequesting());
    return apiService.get(endPoints.credits.activities)
        .then((res) => {
            dispatch(activitiesSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(activitiesError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

/** Daily debit usage by activity for the credits line chart. */
export const fetchCreditUsage = (query = {}) => (dispatch) => {
    dispatch(usageRequesting());
    const qs = qargsToQstring(query);
    return apiService.get(`${endPoints.credits.usage}${qs}`)
        .then((res) => {
            dispatch(usageSuccess(res.data));
            return unwrap(res.data);
        })
        .catch((err) => {
            dispatch(usageError(formatAxiosErrorMessage(err)));
            throw err;
        });
};

export const creditsState = (state) => state.credits;
export default creditsSlice.reducer;
